import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import moment from "moment";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import MasterCardLogo from "../../assets/CardLogo/MasterCardLogo.svg";
import VisaLogo from "../../assets/CardLogo/VisaLogo.svg";
import MirLogo from "../../assets/CardLogo/MirLogo.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-color.svg";
import {
  currentOrganizationSelector,
  userSelector,
} from "../../store/selectors";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import "./PaymentMethodsScreen.css";
import ProfileMainIcon from "../../assets/profile-main.svg";
import ProfileBillingIcon from "../../assets/profile-billing.svg";
import ProfileNotificationsIcon from "../../assets/profile-notifications.svg";
import ProfileSecurityIcon from "../../assets/profile-security.svg";
import desctopIcon from "../../assets/desctopIcon.svg";
import LogoutIcon from "../../assets/logout.svg";
import ProfileTeamIcon from "../../assets/users.svg";
import momentWithLocales from "moment/min/moment-with-locales";
import ProfileActiveSessionsIcon from "../../assets/profile-active-sessions.svg";
import Avatar from "../../components/Avatar/Avatar";
import AppLogo from "../../assets/AppLogoBlue.svg";
import toast from "react-hot-toast";
import {
  getSessionsList,
  payForSubscription,
  resetAllSessions,
} from "../../store/usersSlice";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import useIsMobile from "../../hooks/useIsMobile";
import {
  addOrgPaymentMethod,
  deleteOrgPaymentMethod,
  fetchCurrentOrgSubscription,
  fetchOrgPaymentMethod,
} from "../../store/tariffesSlice";

const CardLogos = {
  MASTERCARD: MasterCardLogo,
  VISA: VisaLogo,
  MIR: MirLogo,
};

const PaymentMethodsScreen = () => {
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [chosenOrgId, setChosenOrgId] = useState(null);
  const currentOrg = useSelector(currentOrganizationSelector);
  const [currentOrganizationTariff, setCurrentOrganizationTariff] =
    useState(null);
  const [paymentMethodsArray, setPaymentMethodsArray] = useState([]);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "7cut • Способы оплаты";
  }, []);

  useEffect(() => {
    const currentOrgId = currentOrg?.id;
    if (!currentOrgId) return;
    setChosenOrgId(currentOrgId);
  }, []);

  useEffect(() => {
    if (!chosenOrgId) return;

    dispatch(fetchOrgPaymentMethod({ orgId: chosenOrgId })).then((res) => {
      if (!res.error) {
        setPaymentMethodsArray(res.payload);
      }
    });
    dispatch(fetchCurrentOrgSubscription({ orgId: chosenOrgId })).then(
      (res) => {
        if (!res.error) {
          setCurrentOrganizationTariff(res.payload);
        }
      }
    );
  }, [chosenOrgId]);

  const handleDeletePaymentMethod = (paymentMethodId) => {
    if (confirm("Вы действительно хотите удалить этот способ оплаты?")) {
      dispatch(
        deleteOrgPaymentMethod({
          orgId: chosenOrgId,
          paymentMethodId,
        })
      ).then((res) => {
        if (!res.error) {
          dispatch(fetchOrgPaymentMethod({ orgId: chosenOrgId })).then(
            (res2) => {
              if (!res2.error) {
                setPaymentMethodsArray(res2.payload);
                toast.success("Способ оплаты удален");
              }
            }
          );
        } else {
          toast.error(res.payload || "Ошибка удаления способа оплаты");
        }
      });
    }
  };

  const renderPaymentMethod = (paymentMethod) => {
    return (
      <div
        style={{
          minHeight: 60,
          border: "1px solid #c1c7d450",
          borderRadius: 8,
          marginTop: 15,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: "12px 24px",
          color: "#c1c7d4",
          fontSize: 16,
        }}
      >
        <span>Банковская карта</span>
        <div
          style={{
            width: 1,
            height: 40,
            marginLeft: 20,
            backgroundColor: "#c1c7d4",
          }}
        />
        <img
          style={{ height: 20, marginLeft: 20 }}
          src={
            paymentMethod.cardType === "UNKNOWN"
              ? MirLogo
              : CardLogos[paymentMethod.cardType]
          }
        />
        <span style={{ fontWeight: 600, marginLeft: 12 }}>
          {paymentMethod.maskedPan.slice(-8)}
        </span>
        <div
          style={{
            width: 1,
            height: 40,
            marginLeft: 20,
            backgroundColor: "#c1c7d4",
          }}
        />
        <DeleteIcon
          onClick={() => handleDeletePaymentMethod(paymentMethod.id)}
          style={{
            marginLeft: 20,
            cursor: "pointer",
            transform: "scale(1.5)",
            stroke: "#c1c7d4",
            color: "#c1c7d4",
          }}
        />
      </div>
    );
  };

  const handleAddPaymentMethod = () => {
    dispatch(addOrgPaymentMethod({ orgId: chosenOrgId })).then((res) => {
      if (!res.error) {
        const link = res.payload.paymentURL;
        if (link) {
          window.location.href = link;
        }
      }
    });
  };

  const handlePayForTrial = (tariffInfo) => {
    dispatch(
      payForSubscription({
        organizationId: chosenOrgId,
        subscriptionId: tariffInfo.id,
      })
    ).then((res) => {
      if (!res.error) {
        const link = res.payload.paymentLink;
        if (link) {
          window.location.href = link;
        }
      } else {
        toast.error(res.payload);
      }
    });
  };

  if (!user) return null;

  return (
    <div className={isMobile ? "App-mobile" : "App"}>
      {/* HEADER */}
      <div
        className={isMobile ? "mainScreenHeader-mobile" : "mainScreenHeader"}
      >
        <div className={isMobile ? "header-left-mobile" : "header-left"}>
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home", { replace: true })}
            className={"mainScreen-logoBlock"}
            style={{ cursor: "pointer" }}
          >
            <img src={AppLogo} style={{ height: "100%" }} alt="logo" />
          </div>
        </div>
        <div className="header-actions">
          <NotificationsPopup />
          <ProfilePopup />
        </div>
      </div>
      {/* LAYOUT: sidebar + main content */}
      <div
        className="payment-methods-container"
        style={{ alignItems: "flex-start" }}
      >
        {/* SIDEBAR */}
        {((!isMobile || isMenuOpen) && (
          <ProfileSidebar activeTab="payment-methods" />
        )) ||
          null}
        {/* MAIN CONTENT */}
        <main
          className={
            isMobile ? "payment-methods-main-mobile" : "payment-methods-main"
          }
        >
          <h1 className="payment-methods-title">Способы оплаты</h1>
          {currentOrganizationTariff && (
            <div>
              <span style={{ color: "#c1c7d4", fontSize: 16 }}>
                Текущий тариф:{" "}
                {`${currentOrganizationTariff.plan.name}${currentOrganizationTariff.status === "TRIALING" ? " (триал до " + moment(currentOrganizationTariff.trialEndDate).format("DD.MM.YYYY") + ")" : " (до " + moment(currentOrganizationTariff.endDate).format("DD.MM.YYYY") + ")"}`}
              </span>
              {currentOrganizationTariff.status === "TRIALING" && (
                <PrimaryButton
                  text={"Оплатить сейчас"}
                  onClick={() => handlePayForTrial(currentOrganizationTariff)}
                  style={{
                    borderRadius: 8,
                    padding: "8px 16px",
                    alignSelf: "flex-start",
                    marginLeft: 16,
                  }}
                />
              )}
            </div>
          )}
          {paymentMethodsArray.map((paymentMethod) =>
            renderPaymentMethod(paymentMethod)
          )}
          <PrimaryButton
            text={"Добавить способ оплаты"}
            onClick={() => handleAddPaymentMethod()}
            style={{
              marginTop: 32,
              borderRadius: 8,
              padding: "12px 24px",
              alignSelf: "flex-start",
            }}
          />
        </main>
      </div>
    </div>
  );
};

export default PaymentMethodsScreen;
